import React from "react";
import { Scene, TourData } from "../../Types";
import {
  editorExperienceStore,
  useCurrentSceneStore,
  useEditingElementStore,
  useParametersStore,
  useViewerStore,
} from "../../api/Zustand";
import * as THREE from "three";
import { UseAssetsQuery, UseDownloadAsset } from "../../api/calls/Queries";
import { useQueryClient } from "@tanstack/react-query";
import { ThreeEvent, useFrame /*, useThree*/ } from "@react-three/fiber";
import { OrbitControlsProps } from "@react-three/drei";
import { useDownloadProgressStore } from "./DownloadProgressBar";
import { useCompassHeadingStore } from "../../state management/zustand/gl-stores";
import { useViewerMapStore } from "../../state management/zustand/viewer-stores";

//
const testMaterial = new THREE.MeshBasicMaterial({
  toneMapped: false,
  //wireframe: true,
  side: THREE.BackSide,
});

const textureLoader = new THREE.TextureLoader();

const canvas = document.createElement("canvas");
const context = canvas.getContext("2d");

canvas.width = 8192;
canvas.height = 4096;

const loadTexture: (url: string) => Promise<void> = async (url: string) => {
  return new Promise((resolve, reject) => {
    textureLoader.load(
      url,
      (t) => {
        if (testMaterial.map !== null) {
          testMaterial.map.dispose();
        }

        const sourceImg = t.source.data as HTMLImageElement;

        if (sourceImg.naturalWidth > 8192) {
          context?.reset();

          context?.drawImage(sourceImg, 0, 0, 8192, 4096);

          const texture = new THREE.CanvasTexture(canvas);

          texture.wrapS = THREE.RepeatWrapping;
          texture.repeat.x = -1;
          texture.mapping = THREE.EquirectangularRefractionMapping;

          console.debug("loading reduced texture");
          testMaterial.map = texture;

          t.dispose();
        } else {
          t.wrapS = THREE.RepeatWrapping;
          t.repeat.x = -1;

          t.mapping = THREE.EquirectangularRefractionMapping;

          testMaterial.map = t;
          testMaterial.needsUpdate = true;
        }
        resolve();
      },
      undefined,
      (err) => {
        console.debug(err);
        reject(err);
      }
    );
  });
};

const resetTexture = () => {
  testMaterial.map = null;
  testMaterial.needsUpdate = true;
};

const WebGLSphere = (props: { scene: Scene; tourData: TourData }) => {
  const { scene, tourData } = props;
  const { setTransition } = editorExperienceStore();

  const { tourID } = useParametersStore();

  const sphereRef = React.useRef<THREE.Mesh>(null);

  const queryClient = useQueryClient();

  React.useLayoutEffect(() => {
    setTransition(true);
  }, [scene.imageID, setTransition]);

  const { setClosed, setOpen } = useViewerMapStore();

  const { viewer } = useViewerStore();

  const { currentSceneID } = useCurrentSceneStore();

  const currentScene = React.useMemo(() => {
    return tourData.scenes.find((one) => one.id === currentSceneID);
  }, [tourData, currentSceneID]);

  React.useEffect(() => {
    if (!currentScene?.imageID) {
      resetTexture();
    }
  }, [currentScene]);

  React.useLayoutEffect(() => {
    if (viewer) {
      // openMap({ isOpen: false });
      setClosed();
      if (currentScene?.map && currentScene.mapAutoOpen) {
        setOpen(currentScene.map);
      }
    } else return;
  }, [viewer, currentScene, setClosed, setOpen]);

  const { setEditingElem } = useEditingElementStore();

  const [lowResKey, setLowResKey] = React.useState<string | undefined>();
  const [hiResKey, setHiResKey] = React.useState<string | undefined>();

  const { data: panos } = UseAssetsQuery(tourID, 1);

  const { setHeading } = useCompassHeadingStore();

  React.useEffect(() => {
    setHiResKey(
      panos?.find((one) => one.assetId === scene.imageID)?.assetKeyFull
    );
    if (panos?.find((one) => one.assetId === scene.imageID)?.assetKeyLow)
      setLowResKey(
        panos?.find((one) => one.assetId === scene.imageID)?.assetKeyLow
      );
  }, [scene.imageID, panos]);

  const { setProgress } = useDownloadProgressStore();

  const { data: hiResBlob } = UseDownloadAsset(hiResKey, setProgress);
  const { data: loResBlob } = UseDownloadAsset(lowResKey);

  const setLowResTexture = React.useCallback(
    async (url: string) => {
      const hiResBlob = queryClient.getQueryData(["DownloadAsset", hiResKey]);

      if (hiResBlob) {
        return;
      } else if (hiResKey) {
        loadTexture(url).then(() => {
          setTransition(false);
          console.debug("low res texture loaded");
          return;
        });
      }
    },
    [setTransition, hiResKey, queryClient]
  );

  const setHiResTexture = React.useCallback(
    async (url: string) => {
      loadTexture(url).then(() => {
        console.debug("hi res texture loaded");

        setTransition(false);

        return;
      });
    },

    [setTransition]
  );

  React.useEffect(() => {
    if (loResBlob) setLowResTexture(loResBlob);
  }, [loResBlob, setLowResTexture]);

  React.useEffect(() => {
    if (hiResBlob) {
      // loadTextureFromCanvas(hiResBlob);
      setHiResTexture(hiResBlob);
    }
  }, [hiResBlob, setHiResTexture]);

  useFrame((state) => {
    const hdn = state.controls as OrbitControlsProps;
    if (hdn.getAzimuthalAngle) {
      setHeading(hdn.getAzimuthalAngle());
    }
  });

  const handlePlace = React.useCallback(
    (e: ThreeEvent<PointerEvent>) => {
      const point = new THREE.Vector3();

      point.copy(e.point);

      return queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        scenes: [
          ...tourData.scenes.map((sc) =>
            sc.id === currentSceneID
              ? {
                  ...sc,
                  hotspots: sc.hotspots.map((hs) =>
                    hs.EDIT_POS === true
                      ? {
                          ...hs,
                          x: e.point.x,
                          y: e.point.y,
                          z: e.point.z,
                          pos: point,
                        }
                      : hs
                  ),
                }
              : sc
          ),
        ],
      });
    },
    [queryClient, tourData, currentSceneID, tourID]
  );

  return (
    <>
      <mesh
        material={testMaterial}
        name="sfera"
        ref={sphereRef}
        onDoubleClick={(e) => {
          e.stopPropagation();

          if (
            tourData?.scenes
              ?.find((one) => one.id === currentSceneID)
              ?.hotspots.some((spot) => spot.EDIT_POS === true)
          ) {
            return;
          } else {
            setEditingElem("scene", JSON.stringify(currentSceneID));
          }
        }}
        onPointerUp={(e) => {
          e.button === 2 && handlePlace(e);
        }}
      >
        <sphereGeometry args={[255, 64, 32]} />
        {/* <meshBasicMaterial
          ref={material}
          side={THREE.DoubleSide}
          toneMapped={false}
          // visible={false}
        /> */}
      </mesh>
    </>
  );
};

export default WebGLSphere;
